import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { 
  Video, 
  Library, 
  Play, 
  FileText,
  TrendingUp,
  Clock,
  Users,
  Star,
  ArrowRight,
  Loader2,
  BookOpenCheck,
  User,
  X,
  Trophy
} from 'lucide-react'
import { useAuth } from '../lib/auth-context'
import Navigation from './Navigation'
import HomeworkWidget from './HomeworkWidget'
import MentorshipSessionWidget from './MentorshipSessionWidget'
import SummitWidget from './SummitWidget'
import AssessmentWidget from './AssessmentWidget'
import ProfileCompletionModal from './ProfileCompletionModal'

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalVideos: 0,
    totalResources: 0,
    totalHomework: 0,
    recentVideos: [],
    recentResources: []
  })
  const [loading, setLoading] = useState(true)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [subscriptionInfo, setSubscriptionInfo] = useState({ status: 'Loading...', color: 'secondary' })
  const { user, subscription, updateUser, loading: authLoading, refreshSubscription } = useAuth()

  useEffect(() => {
    fetchDashboardData()
  }, [])

  // Refresh subscription data if user exists but subscription data is missing
  useEffect(() => {
    // Only refresh if auth is done loading, user exists, but no subscription data
    if (!authLoading && user && !subscription && !user.subscriptionStatus && user.stripeCustomerId) {
      // Small delay to allow auth context to finish, then refresh if needed
      const timer = setTimeout(() => {
        // Double-check conditions before refreshing
        if (!subscription && !user.subscriptionStatus) {
          refreshSubscription()
        }
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [authLoading, user, subscription, refreshSubscription])

  // Update subscription info when user or subscription data becomes available
  useEffect(() => {
    if (authLoading) {
      setSubscriptionInfo({ status: 'Loading...', color: 'secondary' })
      return
    }

    // If no user, show unknown
    if (!user) {
      setSubscriptionInfo({ status: 'Unknown', color: 'secondary' })
      return
    }

    // Get status from subscription object or fallback to user.subscription_status
    const status = subscription?.status || user?.subscriptionStatus || user?.subscription_status
    
    // If no status, show unknown (don't keep showing loading)
    if (!status) {
      setSubscriptionInfo({ status: 'Unknown', color: 'secondary' })
      return
    }
    
    // Calculate days remaining if trial
    let statusText = ''
    let daysRemaining = null
    
    switch (status) {
      case 'active':
        statusText = 'Active'
        setSubscriptionInfo({ status: statusText, color: 'default' })
        return
      case 'trialing':
        statusText = 'Free Trial'
        // Calculate days remaining if trial_end is available
        if (subscription?.trialEnd) {
          const trialEnd = new Date(subscription.trialEnd)
          const today = new Date()
          const diffTime = trialEnd - today
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          if (diffDays > 0) {
            daysRemaining = diffDays
            statusText = `Free Trial (${diffDays} ${diffDays === 1 ? 'day' : 'days'} left)`
          }
        }
        setSubscriptionInfo({ status: statusText, color: 'secondary', daysRemaining })
        return
      case 'past_due':
        statusText = 'Past Due'
        setSubscriptionInfo({ status: statusText, color: 'destructive' })
        return
      case 'canceled':
      case 'cancelled':
        statusText = 'Canceled'
        setSubscriptionInfo({ status: statusText, color: 'destructive' })
        return
      default:
        setSubscriptionInfo({ status: 'Unknown', color: 'secondary' })
        return
    }
  }, [subscription, user, authLoading])

  // Check if user needs to complete profile
  const needsProfileCompletion = user && (!user.firstName || !user.lastName)

  const handleProfileCompletion = (completedUser) => {
    updateUser(completedUser)
    setShowProfileModal(false)
  }

  const handleSkipProfile = () => {
    setShowProfileModal(false)
  }

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken')
      
      // Fetch videos, resources, and homework in parallel
      const [videosResponse, resourcesResponse, homeworkResponse] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/videos`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/resources`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/homework`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      const videosData = videosResponse.ok ? await videosResponse.json() : { videos: [] }
      const resourcesData = resourcesResponse.ok ? await resourcesResponse.json() : { resources: [] }
      const homeworkData = homeworkResponse.ok ? await homeworkResponse.json() : { data: { homework: [] } }

      setStats({
        totalVideos: videosData.videos?.length || 0,
        totalResources: resourcesData.resources?.length || 0,
        totalHomework: homeworkData.data?.homework?.length || 0,
        recentVideos: videosData.videos?.slice(0, 3) || [],
        recentResources: resourcesData.resources?.slice(0, 3) || []
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  // subscriptionInfo is now managed by useEffect above

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
            <p className="text-gray-600 font-medium">Loading dashboard...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Welcome back, {user?.firstName || 'Member'}! 👋
          </h1>
          <p className="text-lg text-gray-600">
            Access your exclusive Impact Accelerator Circle content and resources. We're adding new content and resources continuously, check back regularly for updates.
          </p>
        </div>

        {/* Profile Completion Banner */}
        {needsProfileCompletion && (
          <Card className="mb-8 border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Complete Your Profile</h3>
                    <p className="text-gray-600">Add your name and company to personalize your experience</p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowProfileModal(true)}
                    className="bg-white hover:bg-gray-50 border-orange-200 text-orange-700 hover:text-orange-800"
                  >
                    Complete Setup
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowProfileModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription Status */}
        <Card className="mb-8 border-blue-200/50 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Membership Status</h3>
                  <p className="text-gray-600">Your current subscription</p>
                </div>
              </div>
              <Badge variant={subscriptionInfo.color} className="text-sm px-4 py-2 rounded-xl">
                {subscriptionInfo.status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview - temporarily hidden */}
        {false && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Total Videos</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalVideos}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <Video className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Resources</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalResources}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                    <Download className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Member Since</p>
                    <p className="text-xl font-bold text-gray-900">
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        year: 'numeric' 
                      }) : 'Recently'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Progress</p>
                    <p className="text-xl font-bold text-gray-900">Growing</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Homework</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalHomework}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
                    <BookOpenCheck className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <MentorshipSessionWidget />

          <AssessmentWidget />

          <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer rounded-3xl border-0 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg">
            <Link to="/videos">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Video className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-gray-900">Video Library</CardTitle>
                      <CardDescription className="text-gray-600">Access exclusive training content</CardDescription>
                    </div>
                  </div>
                  <ArrowRight className="w-6 h-6 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6 text-lg">
                  Watch premium videos and training materials designed to accelerate your impact.
                </p>
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                  <Play className="w-5 h-5 mr-2" />
                  Browse Videos
                </Button>
              </CardContent>
            </Link>
          </Card>

          <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer rounded-3xl border-0 bg-gradient-to-br from-green-50 to-green-100 shadow-lg">
            <Link to="/resources">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Library className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-gray-900">Resources</CardTitle>
                      <CardDescription className="text-gray-600">Access exclusive guides, templates, tools and materials.</CardDescription>
                    </div>
                  </div>
                  <ArrowRight className="w-6 h-6 text-green-600" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6 text-lg">
                  Access exclusive guides, templates, tools and materials.
                </p>
                <Button className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                  <FileText className="w-5 h-5 mr-2" />
                  Browse Resources
                </Button>
              </CardContent>
            </Link>
          </Card>

          <HomeworkWidget />

          <SummitWidget />

          {/* Pitch Scorecards widget - hidden for now, can be re-enabled later */}
          {false && (
            <Card className="hover:shadow-xl transition-all duration-300 cursor-pointer rounded-3xl border-0 bg-gradient-to-br from-purple-50 to-indigo-100 shadow-lg">
              <Link to="/pitch-scorecards">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <Trophy className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl text-gray-900">Summit Pitch Founder Scorecards</CardTitle>
                        <CardDescription className="text-gray-600">See evaluations and leaderboard of founders who pitched at the summit</CardDescription>
                      </div>
                    </div>
                    <ArrowRight className="w-6 h-6 text-purple-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-6 text-lg">
                    View detailed pitch evaluations, scores, and investor interest levels from the Impact Accelerator Summit.
                  </p>
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                    <Trophy className="w-5 h-5 mr-2" />
                    View Scorecards
                  </Button>
                </CardContent>
              </Link>
            </Card>
          )}
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Videos */}
          <Card className="rounded-3xl shadow-lg border-0 bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-3 text-xl">
                  <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Video className="w-4 h-4 text-blue-600" />
                  </div>
                  <span>Recent Videos</span>
                </CardTitle>
                <Link to="/videos" className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors">
                  View all
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {stats.recentVideos.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Video className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No videos available yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.recentVideos.map((video) => (
                    <div key={video.id} className="flex items-center space-x-3 p-4 rounded-2xl hover:bg-gray-50 transition-all duration-200">
                      <div className="w-20 h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                        <Play className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 line-clamp-1 mb-1">{video.title}</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-500">
                          {video.duration && (
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>{video.duration}</span>
                            </div>
                          )}
                          {video.category && (
                            <Badge variant="secondary" className="text-xs px-2 py-1 rounded-lg">
                              {video.category}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Resources */}
          <Card className="rounded-3xl shadow-lg border-0 bg-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-3 text-xl">
                  <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center">
                    <Library className="w-4 h-4 text-green-600" />
                  </div>
                  <span>Recent Resources</span>
                </CardTitle>
                <Link to="/resources" className="text-sm text-green-600 hover:text-green-700 font-medium transition-colors">
                  View all
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {stats.recentResources.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Library className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">No resources available yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.recentResources.map((resource) => (
                    <div key={resource.id} className="flex items-center space-x-3 p-4 rounded-2xl hover:bg-gray-50 transition-all duration-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center">
                        <FileText className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 line-clamp-1 mb-1">{resource.title}</h4>
                        <div className="flex items-center space-x-3 text-sm text-gray-500">
                          {resource.file_size && (
                            <span>{Math.round(resource.file_size / 1024)} KB</span>
                          )}
                          {resource.category && (
                            <Badge variant="secondary" className="text-xs px-2 py-1 rounded-lg">
                              {resource.category}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onClose={handleSkipProfile}
        onComplete={handleProfileCompletion}
        user={user}
      />
    </div>
  )
}

export default Dashboard

